/**
 * One-shot cookie banner shown on first visit.
 *
 * AskTogether doesn't use analytics, advertising, or third-party
 * trackers — every cookie / storage item we set is either strictly
 * necessary (attendee token, OAuth session) or set on explicit user
 * action (theme). Under UK PECR we don't need granular consent for
 * strictly-necessary cookies; we still surface the banner once so a
 * first-time visitor isn't blind to what we're storing, and link to
 * the cookies page for the full breakdown.
 *
 * The user's acknowledgement is recorded in localStorage under
 * `STORAGE_KEY` together with a VERSION integer. Bumping the version
 * re-shows the banner — use that when the inventory on /cookies
 * materially changes.
 *
 * Pre-OAuth pages (/invite/:token) and the legal pages themselves
 * (/about /privacy /cookies) skip the banner so the first interaction
 * isn't a modal over policy text.
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "asktogether.cookie_notice";
const VERSION = 1;

function suppressedByPath(): boolean {
  const p = window.location.pathname;
  return (
    p === "/about" ||
    p === "/privacy" ||
    p === "/cookies" ||
    p.startsWith("/invite/")
  );
}

function alreadyAcknowledged(): boolean {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { v?: number };
    return parsed?.v === VERSION;
  } catch {
    return false;
  }
}

export default function CookieBanner() {
  // Render decision is set after mount so SSR / strict-mode replays
  // don't flash the banner for users who've already acknowledged.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (suppressedByPath()) return;
    if (alreadyAcknowledged()) return;
    setVisible(true);
  }, []);

  function acknowledge() {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ v: VERSION, at: new Date().toISOString() }),
      );
    } catch {
      // localStorage blocked — banner will re-appear next visit; safe.
    }
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie notice"
      aria-live="polite"
      className="cookie-banner"
    >
      <div className="cookie-banner-text">
        <strong>Cookies &amp; storage.</strong> AskTogether uses only
        cookies and browser storage that are strictly necessary to run
        the service you asked for (joining a session, staying signed in
        to the dashboard), plus your theme preference. No advertising,
        no analytics, no third-party trackers. Full details on the{" "}
        <a href="/cookies">cookies page</a>.
      </div>
      <div className="cookie-banner-actions">
        <button type="button" onClick={acknowledge}>
          Got it
        </button>
      </div>
    </div>
  );
}
